import * as React from "react";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Stack, LinearProgress } from "@mui/material";
import { useDeleteContactMutation } from "../list-manager/api";
import { useQueryClient } from "@tanstack/react-query";
import { BeatLoader } from "react-spinners";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

type DeleteContactModalProps = {
    list: List | undefined;
    contact: Contact;
    onDeleteFinished: () => void;
};

export default function DeleteContactModal({
  list,
  contact,
  onDeleteFinished,
}: DeleteContactModalProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = React.useState(false);

  const { mutate: deleteMutate, isPending: deletePending, isSuccess: deleteSuccess, reset: resetDeleteMutation } =
    useDeleteContactMutation({
      onSuccess: () => {
        onDeleteFinished();
        queryClient.invalidateQueries({queryKey: ["contacts"]})
      },
    });

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    resetDeleteMutation();
  };

  const name =
    contact?.first_name && contact?.last_name
      ? `${contact?.first_name?.toLowerCase()} ${contact?.last_name?.toLowerCase()}`
      : `Contact ID: ${contact.id}`;

  return (
    <React.Fragment>
      <IconButton
        size="small"
        color="error"
        aria-label="delete"
        onClick={handleClickOpen}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          Remove Contact
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <Stack width={400} padding={3} alignItems="center">
            {deletePending ? (
              <LinearProgress style={{ width: "100%" }} />
            ) : deleteSuccess ? (
              <Typography
                style={{
                  textAlign: "center",
                  fontSize: 14,
                  fontWeight: "bold",
                  textTransform: "capitalize",
                }}
              >
                {name} was successfully removed from list {list?.name}
              </Typography>
            ) : (
              <Typography
                style={{
                  textAlign: "center",
                  fontSize: 14,
                  fontWeight: "bold",
                  textTransform: "capitalize",
                }}
              >
                Are you sure you want to remove {name}
                <br /> from the list: {list?.name}?
              </Typography>
            )}
          </Stack>
        </DialogContent>
        {deleteSuccess ? (
          <DialogActions>
            <Button onClick={handleClose} disabled={deletePending}>
              Close
            </Button>
          </DialogActions>
        ) : (
          <DialogActions>
            <Button onClick={handleClose} disabled={deletePending}>
              Cancel
            </Button>
            <Button
              autoFocus
              onClick={() => deleteMutate({ contact_id: contact.id })}
              disabled={deletePending}
              color="error"
            >
              Confirm Remove
            </Button>
          </DialogActions>
        )}
      </BootstrapDialog>
    </React.Fragment>
  );
}
